import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, unref as _unref, openBlock as _openBlock, createElementBlock as _createElementBlock, normalizeClass as _normalizeClass, isRef as _isRef, vModelText as _vModelText, withDirectives as _withDirectives, toDisplayString as _toDisplayString, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, withModifiers as _withModifiers, withCtx as _withCtx, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "flex-1 overflow-y-auto" }
const _hoisted_2 = { class: "grid grid-cols-2 gap-4" }
const _hoisted_3 = { class: "flex" }
const _hoisted_4 = ["disabled"]
const _hoisted_5 = ["disabled"]
const _hoisted_6 = ["disabled"]
const _hoisted_7 = ["disabled"]
const _hoisted_8 = {
  key: 0,
  class: "mt-1 text-sm text-red-600"
}
const _hoisted_9 = { class: "border border-gray-200 rounded-lg" }
const _hoisted_10 = { class: "max-h-[150px] overflow-y-auto" }
const _hoisted_11 = { class: "min-w-full divide-y divide-gray-200" }
const _hoisted_12 = { class: "bg-white divide-y divide-gray-200" }
const _hoisted_13 = { key: 0 }
const _hoisted_14 = { class: "px-4 py-4 text-sm w-1/4" }
const _hoisted_15 = { class: "flex items-center" }
const _hoisted_16 = {
  key: 0,
  class: "bg-red-100 text-red-800 px-2 py-1 rounded"
}
const _hoisted_17 = {
  key: 1,
  class: "mx-2"
}
const _hoisted_18 = {
  key: 2,
  class: "bg-green-100 text-green-800 px-2 py-1 rounded"
}
const _hoisted_19 = { class: "px-4 py-4 text-sm w-1/4" }
const _hoisted_20 = { class: "flex items-center" }
const _hoisted_21 = {
  key: 0,
  class: "bg-red-100 text-red-800 px-2 py-1 rounded"
}
const _hoisted_22 = {
  key: 1,
  class: "mx-2"
}
const _hoisted_23 = {
  key: 2,
  class: "bg-green-100 text-green-800 px-2 py-1 rounded"
}
const _hoisted_24 = { class: "px-4 py-4 text-sm text-gray-500 w-1/4" }
const _hoisted_25 = { class: "flex justify-end space-x-3" }
const _hoisted_26 = ["disabled"]

import { ref, computed, watch } from 'vue';
import { VaccinationProgressEntry } from '@/types/v2';
import { useVaccinationForm } from '@/composables/tasks/vaccination/useVaccinationForm';
import { useTaskHistory } from '@/composables/tasks/useTaskHistory';

import ModalBox from "@/components/v2/Modal/ModalBox.vue";
import { useVaccination } from "@/composables/tasks/vaccination/useVaccination";
import { useRecord } from "@/composables/tasks/vaccination/useVaccinationRecords";
import { useVaccinationConfigurations } from "@/composables/tasks/vaccination/useVaccinationConfigurations";


export default /*@__PURE__*/_defineComponent({
  __name: 'VaccinationRevisionModal',
  props: {
    isOpen: { type: Boolean },
    entry: {},
    configKey: {},
    entryIndex: {},
    onClose: { type: Function }
  },
  setup(__props: any) {

const props = __props;

// ===================================================================
// Composables
// ===================================================================
const { updateVaccinationRecord, addRevisionToRecord } = useRecord();
const { getVaccinationRecordHistory } = useVaccinationConfigurations();
const { configurations } = useVaccination();

const {
    vaccinated,
    weight,
    comment: reason,
    isWeightDisabled,
    isVaccinatedDisabled,
    incrementVaccinated,
    resetForm
} = useVaccinationForm({
    allowZeroVaccinated: true,
    enforceWeightVaccinatedLink: false
});

const { recordChange } = useTaskHistory();

// ===================================================================
// State
// ===================================================================
const originalEntry = ref<VaccinationProgressEntry | null>(null);
const configIndex = ref(-1);
const errors = ref({
    reason: ''
});

// ===================================================================
// Computed
// ===================================================================
const hasChanges = computed(() => {
    if (!originalEntry.value) return false;

    const vaccinatedChanged = vaccinated.value !== originalEntry.value.vaccinated;
    const weightChanged = (weight.value || 0) !== (originalEntry.value.weight || 0);

    return vaccinatedChanged || weightChanged;
});

// Update isValid to be more permissive for valid changes
const isValid = computed(() => {
    // Allow revising down to 0 (e.g. death of fish correction)
    const hasValidValues =
        typeof vaccinated.value === 'number' &&
        Number.isFinite(vaccinated.value) &&
        vaccinated.value >= 0 &&
        typeof weight.value === 'number' &&
        Number.isFinite(weight.value) &&
        weight.value >= 0;

    // If there are changes, reason is required
    const hasValidReason = !hasChanges.value || (hasChanges.value && reason.value.trim() !== '');

    return hasValidValues && hasValidReason && !errors.value.reason;
});

// Format and sort history entries
const formattedHistory = computed(() => {
    if (!props.configKey || props.entryIndex === -1) return [];

    const entries = getVaccinationRecordHistory(props.configKey, props.entryIndex);

    // Process and format entries for display
    const formatted = entries.map(entry => {
        let result = {
            field: entry.field,
            reason: entry.reason,
            timestamp: entry.timestamp,
            oldIvps: undefined,
            newIvps: undefined,
            oldWeight: undefined,
            newWeight: undefined
        };

        // Extract values based on the field type
        if (entry.field === 'IVP Administration' && typeof entry.oldValue === 'object' && typeof entry.newValue === 'object') {
            result.oldIvps = entry.oldValue.vaccinated;
            result.newIvps = entry.newValue.vaccinated;
            result.oldWeight = entry.oldValue.weight;
            result.newWeight = entry.newValue.weight;
        } else if (entry.field === 'Administered IVPs') {
            result.oldIvps = entry.oldValue;
            result.newIvps = entry.newValue;
        } else if (entry.field === 'Weight') {
            result.oldWeight = entry.oldValue;
            result.newWeight = entry.newValue;
        }

        return result;
    });

    // Sort by timestamp, newest first
    return formatted.sort((a, b) => {
        return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
    });
});

// ===================================================================
// Methods
// ===================================================================
const setEntry = (entry: VaccinationProgressEntry) => {
    originalEntry.value = {...entry}; // Create a copy to avoid reference issues

    // Set form values
    vaccinated.value = entry.vaccinated ?? 1; // Use nullish coalescing
    weight.value = entry.weight ?? 0;
    reason.value = '';

    // Reset errors
    errors.value.reason = '';
};

const validate = () => {
    // Reset errors
    errors.value.reason = '';

    // Validate reason if there are changes
    if (hasChanges.value && !reason.value.trim()) {
        errors.value.reason = 'Reason for revision is required';
        return false;
    }

    return true;
};

const decrementVaccinatedRevision = () => {
    if (isVaccinatedDisabled.value) return;
    if (vaccinated.value > 0) {
        vaccinated.value--;
    }
};

const handleSubmit = async () => {
    if (!validate() || !props.entry || !hasChanges.value || !isValid.value) {
        return;
    }

    const oldVaccinated = originalEntry.value?.vaccinated;
    const oldWeight = originalEntry.value?.weight;

    if (oldVaccinated === vaccinated.value && oldWeight === weight.value) {
        return;
    }

    // Create a single record with both changes
    const success = await recordChange({
        fieldPath: `configurations[${configIndex.value}].progressData[${props.entryIndex}]`,
        fieldName: 'IVP Administration',
        oldValue: {
            vaccinated: oldVaccinated,
            weight: oldWeight
        },
        newValue: {
            vaccinated: vaccinated.value,
            weight: weight.value
        },
        context: 'VaccinationProgress',
        contextType: 'edit',
        workflowStage: 'execution',
        requireJustification: false,
        justification: reason.value
    });

    if (success) {
        addRevisionToRecord(
            props.configKey,
            props.entryIndex,
            {vaccinated: vaccinated.value, weight: weight.value},
    {vaccinated: oldVaccinated, weight: oldWeight},
            reason.value
        );

        updateVaccinationRecord(props.configKey, props.entryIndex, {
            vaccinated: vaccinated.value,
            weight: weight.value
        });

        handleClose();
    }
};

const handleClose = () => {
    resetForm();
    originalEntry.value = null;
    errors.value.reason = ''
    if (props.onClose) {
        props.onClose();
    }
};

// ===================================================================
// Watchers
// ===================================================================
watch(() => props.isOpen, (isOpen) => {
    if (isOpen && props.entry) {
        setEntry(props.entry);
    }
});

watch(() => props.configKey, (key) => {
    if (!key) {
        console.error('No configuration key provided');
        return;
    }

    const configs = configurations.value;

    // Defensive check for configurations
    if (!configs || configs.length === 0) {
        console.error('No configurations available');
        return;
    }

    // Find the exact matching configuration
    const foundConfig = configs.find(config => config.key === key);

    if (!foundConfig) {
        console.error(`No configuration found for key: ${key}`);

        configIndex.value = -1;
        if (props.onClose) props.onClose();

        return;
    }

    // Find the index of the matched configuration
    configIndex.value = configs.indexOf(foundConfig);
}, {immediate: true});

return (_ctx: any,_cache: any) => {
  return (_ctx.isOpen)
    ? (_openBlock(), _createBlock(ModalBox, {
        key: 0,
        onClose: handleClose
      }, {
        header: _withCtx(() => _cache[4] || (_cache[4] = [
          _createElementVNode("h2", { class: "text-xl font-bold text-gray-800" }, "Update IVP Administration Entry", -1)
        ])),
        main: _withCtx(() => [
          _createElementVNode("div", _hoisted_1, [
            _createElementVNode("form", {
              class: "space-y-6",
              onSubmit: _withModifiers(handleSubmit, ["prevent"])
            }, [
              _createElementVNode("div", _hoisted_2, [
                _createElementVNode("div", null, [
                  _cache[7] || (_cache[7] = _createElementVNode("label", { class: "block text-sm font-medium text-gray-700 mb-1" }, " No. of Administered IVPs ", -1)),
                  _createElementVNode("div", _hoisted_3, [
                    _createElementVNode("button", {
                      class: _normalizeClass([{ 'opacity-50 cursor-not-allowed': _unref(isVaccinatedDisabled) || _unref(vaccinated) <= 0 }, "p-2 border border-primary rounded-l-md bg-primary text-white"]),
                      disabled: _unref(isVaccinatedDisabled) || _unref(vaccinated) <= 0,
                      type: "button",
                      onClick: decrementVaccinatedRevision
                    }, _cache[5] || (_cache[5] = [
                      _createElementVNode("svg", {
                        class: "h-4 w-4",
                        fill: "currentColor",
                        viewBox: "0 0 20 20",
                        xmlns: "http://www.w3.org/2000/svg"
                      }, [
                        _createElementVNode("path", {
                          "clip-rule": "evenodd",
                          d: "M3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z",
                          "fill-rule": "evenodd"
                        })
                      ], -1)
                    ]), 10, _hoisted_4),
                    _withDirectives(_createElementVNode("input", {
                      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => (_isRef(vaccinated) ? (vaccinated).value = $event : null)),
                      class: _normalizeClass([{ 'opacity-50 cursor-not-allowed': _unref(isVaccinatedDisabled) }, "w-full px-3 py-2 border-t border-b border-gray-300 focus:outline-none focus:ring-primary focus:border-primary text-center"]),
                      disabled: _unref(isVaccinatedDisabled),
                      type: "number"
                    }, null, 10, _hoisted_5), [
                      [
                        _vModelText,
                        _unref(vaccinated),
                        void 0,
                        { number: true }
                      ]
                    ]),
                    _createElementVNode("button", {
                      class: _normalizeClass([{ 'opacity-50 cursor-not-allowed': _unref(isVaccinatedDisabled) }, "p-2 border border-primary rounded-r-md bg-primary text-white"]),
                      disabled: _unref(isVaccinatedDisabled),
                      type: "button",
                      onClick: _cache[1] || (_cache[1] = 
//@ts-ignore
(...args) => (_unref(incrementVaccinated) && _unref(incrementVaccinated)(...args)))
                    }, _cache[6] || (_cache[6] = [
                      _createElementVNode("svg", {
                        class: "h-4 w-4",
                        fill: "currentColor",
                        viewBox: "0 0 20 20",
                        xmlns: "http://www.w3.org/2000/svg"
                      }, [
                        _createElementVNode("path", {
                          "clip-rule": "evenodd",
                          d: "M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z",
                          "fill-rule": "evenodd"
                        })
                      ], -1)
                    ]), 10, _hoisted_6)
                  ])
                ]),
                _createElementVNode("div", null, [
                  _cache[8] || (_cache[8] = _createElementVNode("label", { class: "block text-sm font-medium text-gray-700 mb-1" }, " Weight (Grams) ", -1)),
                  _withDirectives(_createElementVNode("input", {
                    "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => (_isRef(weight) ? (weight).value = $event : null)),
                    class: _normalizeClass([{ 'opacity-50 cursor-not-allowed': _unref(isWeightDisabled) }, "w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"]),
                    disabled: _unref(isWeightDisabled),
                    type: "number"
                  }, null, 10, _hoisted_7), [
                    [
                      _vModelText,
                      _unref(weight),
                      void 0,
                      { number: true }
                    ]
                  ])
                ])
              ]),
              _createElementVNode("div", null, [
                _cache[9] || (_cache[9] = _createElementVNode("label", { class: "block text-sm font-medium text-gray-700 mb-1" }, " Reason for revision ", -1)),
                _withDirectives(_createElementVNode("textarea", {
                  "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => (_isRef(reason) ? (reason).value = $event : null)),
                  class: _normalizeClass([{ 'border-red-500': errors.value.reason }, "w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"]),
                  rows: "3"
                }, null, 2), [
                  [_vModelText, _unref(reason)]
                ]),
                (errors.value.reason)
                  ? (_openBlock(), _createElementBlock("p", _hoisted_8, _toDisplayString(errors.value.reason), 1))
                  : _createCommentVNode("", true)
              ]),
              _createElementVNode("div", null, [
                _cache[12] || (_cache[12] = _createElementVNode("h3", { class: "text-lg font-medium text-gray-900 mb-3" }, "Revisions:", -1)),
                _createElementVNode("div", _hoisted_9, [
                  _cache[11] || (_cache[11] = _createElementVNode("div", { class: "w-full" }, [
                    _createElementVNode("table", { class: "min-w-full divide-y divide-gray-200" }, [
                      _createElementVNode("thead", { class: "bg-gray-50" }, [
                        _createElementVNode("tr", { class: "border-b" }, [
                          _createElementVNode("th", {
                            class: "px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/4",
                            scope: "col"
                          }, " Adm. IVPs "),
                          _createElementVNode("th", {
                            class: "px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/4",
                            scope: "col"
                          }, " Weight "),
                          _createElementVNode("th", {
                            class: "px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/4",
                            scope: "col"
                          }, " Justification ")
                        ])
                      ])
                    ])
                  ], -1)),
                  _createElementVNode("div", _hoisted_10, [
                    _createElementVNode("table", _hoisted_11, [
                      _createElementVNode("tbody", _hoisted_12, [
                        (formattedHistory.value.length === 0)
                          ? (_openBlock(), _createElementBlock("tr", _hoisted_13, _cache[10] || (_cache[10] = [
                              _createElementVNode("td", {
                                class: "px-4 py-4 text-sm text-center text-gray-500",
                                colspan: "4"
                              }, " No revision history available ", -1)
                            ])))
                          : _createCommentVNode("", true),
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(formattedHistory.value, (item, index) => {
                          return (_openBlock(), _createElementBlock("tr", {
                            key: index,
                            class: "hover:bg-gray-50"
                          }, [
                            _createElementVNode("td", _hoisted_14, [
                              _createElementVNode("span", _hoisted_15, [
                                (item.oldIvps !== undefined)
                                  ? (_openBlock(), _createElementBlock("span", _hoisted_16, _toDisplayString(item.oldIvps), 1))
                                  : _createCommentVNode("", true),
                                (item.oldIvps !== undefined && item.newIvps !== undefined)
                                  ? (_openBlock(), _createElementBlock("span", _hoisted_17, "→"))
                                  : _createCommentVNode("", true),
                                (item.newIvps !== undefined)
                                  ? (_openBlock(), _createElementBlock("span", _hoisted_18, _toDisplayString(item.newIvps), 1))
                                  : _createCommentVNode("", true)
                              ])
                            ]),
                            _createElementVNode("td", _hoisted_19, [
                              _createElementVNode("span", _hoisted_20, [
                                (item.oldWeight !== undefined)
                                  ? (_openBlock(), _createElementBlock("span", _hoisted_21, _toDisplayString(item.oldWeight || 'N/A'), 1))
                                  : _createCommentVNode("", true),
                                (item.oldWeight !== undefined && item.newWeight !== undefined)
                                  ? (_openBlock(), _createElementBlock("span", _hoisted_22, "→"))
                                  : _createCommentVNode("", true),
                                (item.newWeight !== undefined)
                                  ? (_openBlock(), _createElementBlock("span", _hoisted_23, _toDisplayString(item.newWeight || 'N/A'), 1))
                                  : _createCommentVNode("", true)
                              ])
                            ]),
                            _createElementVNode("td", _hoisted_24, _toDisplayString(item.reason || 'No justification provided'), 1)
                          ]))
                        }), 128))
                      ])
                    ])
                  ])
                ])
              ])
            ], 32)
          ])
        ]),
        footer: _withCtx(() => [
          _createElementVNode("div", _hoisted_25, [
            _createElementVNode("button", {
              class: "px-4 py-2 bg-white border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary",
              type: "button",
              onClick: handleClose
            }, " Cancel "),
            _createElementVNode("button", {
              class: _normalizeClass([{ 'opacity-50 cursor-not-allowed': !hasChanges.value || !isValid.value }, "px-4 py-2 bg-primary border border-transparent rounded-md text-sm font-medium text-white hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"]),
              disabled: !hasChanges.value || !isValid.value,
              type: "button",
              onClick: handleSubmit
            }, " Save Changes ", 10, _hoisted_26)
          ])
        ]),
        _: 1
      }))
    : _createCommentVNode("", true)
}
}

})